import * as React from 'react';

import {
  Card,
  CardActions,
  CardContent,
  createStyles,
  Grid,
  Theme,
  Typography,
  withStyles,
  WithStyles,
  CardMedia,
} from '@nelnet/unifi-components-react';

import Link from '../modules/Link';
import usePlatformVersion from '../hooks/usePlatformVersion';

const styles = ({ palette }: Theme) =>
  createStyles({
    wrapper: {
      margin: '0 auto',
      '& p': {
        maxWidth: '720px',
      },
    },
    card: {
      display: 'flex',
      flexGrow: 1,
    },
    cardMedia: {
      flexBasis: '30%',
      backgroundColor: palette.grey[200],
      display: 'flex',
      justifyContent: 'center',
      '& svg': {
        width: '75%',
      },
    },
    cardContent: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
    },
  });

interface IPlatformCardGrid extends WithStyles<typeof styles> {
  link?: string;
}

const PlatformCardGrid: React.FunctionComponent<IPlatformCardGrid> = ({ classes, link }) => {
  const angularVersion = usePlatformVersion('angular');
  const reactVersion = usePlatformVersion('react');
  const webVersion = usePlatformVersion('web');

  const angularLink = '';
  let reactLink = '';
  let webLink = '';
  let linkText = '';
  let additionalLinkText = '';
  const gridMargin = link === 'components' ? 'auto' : 'undefined';

  switch (link) {
    case 'components':
      // angularLink = 'https://angular.unifi.nelnet.io/components/';
      reactLink = '/react/components/';
      webLink = '/web/components/';
      linkText = 'Explore';
      break;
    case 'getstarted':
      // angularLink = '/getstarted/angular/';
      reactLink = '/getstarted/react/';
      webLink = '/getstarted/web/';
      linkText = 'Get Started with ';
      break;
    case 'theming':
      // angularLink = '/theming/angular/';
      reactLink = '/theming/react/';
      webLink = '/theming/web/';
      linkText = 'View';
      additionalLinkText = 'Docs';
      break;
    case 'changelog':
      // angularLink = '/changelog/angular/';
      reactLink = '/changelog/react/';
      webLink = '/changelog/web/';
      linkText = 'View';
      additionalLinkText = 'Logs';
      break;
  }

  return (
    <Grid container constrained spacing={3} component="section" margin={{ y: 3, x: gridMargin }}>
      <Grid item container md={4}>
        <Card className={classes.card}>
          <CardMedia className={classes.cardMedia}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 250 250"
              aria-hidden="true"
              focusable="false"
              style={{ transform: 'scale(1.2)' }}
            >
              <path fill="#DD0031" d="M125 30L32 63l14 123 79 44 79-44 14-123z" />
              <path fill="#C3002F" d="M125 30v200l79-44 14-123-93-33z" />
              <path
                fill="#FFF"
                d="M125 52L67 183h22l11-30h50l11 30h22L125 52zm17 83h-34l17-40 17 40z"
              />
            </svg>
          </CardMedia>
          <div>
            <CardContent className={classes.cardContent}>
              <Link variant="h3" href={angularLink}>
                Unifi Angular
              </Link>
              <Typography color="textSecondary">Version {angularVersion}</Typography>
            </CardContent>
            <CardActions>
              <Link variant="body1" href={angularLink} padding={1}>
                {linkText} Angular {additionalLinkText}
              </Link>
            </CardActions>
          </div>
        </Card>
      </Grid>
      <Grid item container md={4}>
        <Card className={classes.card}>
          <CardMedia className={classes.cardMedia}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 842 595"
              aria-hidden="true"
              focusable="false"
              style={{ transform: 'scale(1.75)', fill: '#61DAFB' }}
            >
              <path d="M666 297c0-33-40-64-103-83 15-64 8-114-20-130-7-4-14-6-22-6v22c4 0 8 1 11 3 14 8 19 37 15 76l-5 29c-20-5-41-8-64-11-13-18-27-35-41-50 32-30 63-47 84-47V78c-28 0-64 20-100 54-37-34-73-54-100-54v23c21 0 51 16 84 46-14 15-28 32-41 50-23 3-44 6-64 11l-5-29c-5-38 1-68 14-76l12-2V79c-9 0-16 1-23 5-28 16-34 67-20 130-62 19-102 50-102 82 0 33 40 64 103 83-15 63-8 114 20 130 6 4 14 6 22 6 28 0 64-20 100-54 37 34 73 53 100 53 9 1 16-1 23-5 28-16 34-67 20-130 62-19 102-50 102-82zm-130-67l-13 39a473 473 0 00-28-47l41 8zm-46 106c-7 14-15 27-24 39a520 520 0 01-90 0 551 551 0 01-45-78 521 521 0 0145-78 520 520 0 0190 0 551 551 0 0145 77c-6 14-13 27-21 40zm33-13c5 14 10 27 13 40l-41 8a552 552 0 0028-48zM421 430l-28-32a619 619 0 0056 0c-9 12-19 22-28 32zm-74-59l-41-8 13-39a473 473 0 0028 47zm74-208l27 32a619 619 0 00-55 0c9-12 19-22 28-32zm-74 59a552 552 0 00-28 48c-5-14-10-27-13-40l41-8zm-91 125c-35-15-58-35-58-50s23-36 58-51l28-10c6 19 13 40 22 61-9 20-16 41-22 60l-28-10zm54 143c-14-8-19-37-15-76l5-29c20 5 41 8 64 11 13 18 27 35 41 50-32 30-63 47-84 47-4 0-8-1-11-3zm237-76c5 38-1 68-14 76l-12 2c-21 0-51-16-84-46 14-15 28-32 41-50 23-3 44-6 64-11l5 29zm39-67l-28 10c-6-19-13-40-22-61 9-20 16-41 22-60l28 10c35 15 58 35 58 51 0 15-23 35-58 50zM321 78z" />
              <circle cx="420.9" cy="296.5" r="45.7" />
              <path d="M521 78z" />
            </svg>
          </CardMedia>
          <div>
            <CardContent className={classes.cardContent}>
              <Link variant="h3" href={reactLink}>
                Unifi React
              </Link>
              <Typography color="textSecondary">Version {reactVersion}</Typography>
            </CardContent>
            <CardActions>
              <Link variant="body1" href={reactLink} padding={1}>
                {linkText} React {additionalLinkText}
              </Link>
            </CardActions>
          </div>
        </Card>
      </Grid>
      <Grid item container md={4}>
        <Card className={classes.card}>
          <CardMedia className={classes.cardMedia}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 451 433"
              aria-hidden="true"
              focusable="false"
              style={{ transform: 'scale(0.9)' }}
            >
              <path
                fill="#D84018"
                d="M225 0L0 1l40 383 185 49 186-45L451 1zm-55 154l-76 39 76 34v30L59 203v-18l111-61zm42 128h-31l61-180h31zm182-79l-111 54v-30l77-34-77-39v-30l111 61z"
              />
            </svg>
          </CardMedia>
          <div>
            <CardContent className={classes.cardContent}>
              <Link variant="h3" href={webLink}>
                Unifi Web
              </Link>
              <Typography color="textSecondary">Version {webVersion}</Typography>
            </CardContent>
            <CardActions>
              <Link variant="body1" href={webLink} padding={1}>
                {linkText} Web {additionalLinkText}
              </Link>
            </CardActions>
          </div>
        </Card>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(PlatformCardGrid);
